import { useState } from 'react'
import { ClipboardPlus } from 'lucide-react'
import { INQUIRY_PURPOSES, inquiryPurposeLabel, type FreeInspectionInput } from '@ghp/domain'
import { Button, Field, FunnelStep, Input, Modal, Select, useFunnel } from '@ghp/ui'

import s from './FreeInspectionDialog.module.css'

const PURPOSE_OPTIONS = INQUIRY_PURPOSES.map((purpose) => ({
  value: purpose,
  label: inquiryPurposeLabel(purpose) ?? purpose,
}))

const EMPTY = { address: '', clientName: '', occupantName: '' }

const filled = (text: string): string | null => (text.trim() === '' ? null : text.trim())

const summaryOf = (...parts: (string | null | undefined)[]) =>
  parts.filter((part) => part !== null && part !== undefined && part !== '').join(' · ')

interface FreeInspectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Ouvre la fiche — rien ne part au réseau : le contrôle se transmet comme les autres. */
  onStart: (input: FreeInspectionInput) => void
}

/**
 * La **fiche courte** d'un contrôle libre (#419, M41) : l'agent est devant une maison qu'aucune
 * demande ne nomme. Les étapes de « Nouvelle demande » au bureau (entonnoir de #533), **aucun champ
 * obligatoire** (M21) — le bureau complétera au rattachement. Sans adresse, le pied le dit sans
 * fermer « Commencer le contrôle ».
 */
export function FreeInspectionDialog({ open, onOpenChange, onStart }: FreeInspectionDialogProps) {
  const [fields, setFields] = useState(EMPTY)
  const [purpose, setPurpose] = useState<string | null>(null)

  const close = () => {
    setFields(EMPTY)
    setPurpose(null)
    onOpenChange(false)
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      icon={<ClipboardPlus size={18} />}
      title="Contrôle libre"
      subtitle="Un contrôle sans demande : le bureau le rattachera après la transmission. Rien n'est obligatoire."
      footer={
        <>
          {filled(fields.address) === null && (
            <span className={s.missing}>Il manque : l’adresse</span>
          )}
          <Button
            tone="brand"
            onClick={() => {
              onStart({
                address: filled(fields.address),
                clientName: filled(fields.clientName),
                occupantName: filled(fields.occupantName),
                purpose,
              })
              close()
            }}
          >
            Commencer le contrôle
          </Button>
          <Button tone="ghost" onClick={close}>
            Annuler
          </Button>
        </>
      }
    >
      {/* La fenêtre fermée est démontée : elle se rouvre sur la première étape. */}
      <Steps fields={fields} setFields={setFields} purpose={purpose} setPurpose={setPurpose} />
    </Modal>
  )
}

function Steps({
  fields,
  setFields,
  purpose,
  setPurpose,
}: {
  fields: typeof EMPTY
  setFields: (update: (current: typeof EMPTY) => typeof EMPTY) => void
  purpose: string | null
  setPurpose: (purpose: string | null) => void
}) {
  const step = useFunnel(3)

  return (
    <div className={s.steps}>
      <FunnelStep
        title="Identification"
        {...step(0, summaryOf(PURPOSE_OPTIONS.find((option) => option.value === purpose)?.label))}
      >
        <div className={s.fields}>
          <Field label="Type de contrôle">
            {({ labelId }) => (
              <Select
                aria-labelledby={labelId}
                placeholder="Type inconnu"
                options={PURPOSE_OPTIONS}
                value={purpose}
                onValueChange={setPurpose}
              />
            )}
          </Field>
        </div>
      </FunnelStep>
      <FunnelStep title="Adresse à contrôler" {...step(1, summaryOf(filled(fields.address)))}>
        <div className={s.fields}>
          <Field label="Adresse" hint="Numéro, rue et ville, comme vous les lisez sur place.">
            {({ id, hintId }) => (
              <Input
                id={id}
                aria-describedby={hintId}
                value={fields.address}
                onValueChange={(address) => setFields((current) => ({ ...current, address }))}
              />
            )}
          </Field>
        </div>
      </FunnelStep>
      <FunnelStep
        title="Donneur d’ordre & contact"
        {...step(2, summaryOf(filled(fields.clientName), filled(fields.occupantName)))}
      >
        <div className={s.fields}>
          <Field label="Client">
            {({ id }) => (
              <Input
                id={id}
                value={fields.clientName}
                onValueChange={(clientName) => setFields((current) => ({ ...current, clientName }))}
              />
            )}
          </Field>
          <Field label="Occupant">
            {({ id }) => (
              <Input
                id={id}
                value={fields.occupantName}
                onValueChange={(occupantName) =>
                  setFields((current) => ({ ...current, occupantName }))
                }
              />
            )}
          </Field>
        </div>
      </FunnelStep>
    </div>
  )
}
